/**
 * client/game/killcam-time.ts — killcam playback clock (pure). OWNER: integration.
 *
 * A killcam plays archived footage of server time [deathTime - camtime, deathTime + postdelay] from the attacker's
 * view (_killcam.gsc killcam(): archivetime = killcamoffset = camtime + predelay, so the spectator view runs
 * `killcamoffset` behind live and starts at deathtime - camtime; it lasts killcamlength = camtime + postdelay).
 *
 *  - Respawn killcam (_killcam.gsc:172-260, 580-625): camtime 2.5 + postdelay 2, real time (no slow motion).
 *  - Final killcam (_killcam.gsc:470-556 finalkillcam): camtime 5.0 (`!respawn`), killcamlength = camtime +
 *    postdelay - 0.05, and waitfinalkillcamslowdown (_killcam.gsc:268-282):
 *        secondsuntildeath = (deathtime - killcamstarttime) / 1000      (= camtime)
 *        wait( max(0, secondsuntildeath - 2) );   setslowmotion( 1.0, 0.25, 2 );
 *        wait( 2 + 0.5 );                         setslowmotion( 0.25, 1, 1.0 );
 *    The thread starts after the `wait 0.05` in finalkillcam().
 *
 * ESTIMATE (engine, not in the scripts): GSC `wait` runs on server (game) time, which setslowmotion scales, while the
 * setslowmotion lerp itself runs over REAL seconds. With those two rules the final killcam lasts about 11.8 s of
 * real time for 6.95 s of footage, and the death plays at a quarter speed.
 */

/** waitfinalkillcamslowdown constants (_killcam.gsc:268-282). */
export const FINAL_SLOWMO = {
  /** The thread starts after finalkillcam()'s `wait 0.05`. */
  threadStartS: 0.05,
  /** waitbeforedeath = 2: slow-down starts this long (game time) before the death. */
  waitBeforeDeathS: 2,
  /** setslowmotion( 1.0, 0.25, 2 ). */
  slowScale: 0.25,
  rampDownS: 2,
  /** wait( waitbeforedeath + 0.5 ) after the first setslowmotion, in game time. */
  holdS: 2.5,
  /** setslowmotion( 0.25, 1, 1.0 ). */
  rampUpS: 1,
} as const;

/** Real-time segment of the slow-motion curve: game time advances at a scale lerped from s0 to s1 over `durS` real s. */
interface Seg { r0: number; durS: number; g0: number; s0: number; s1: number }

/**
 * Footage (game) seconds since the final killcam started, as a function of real seconds since it started.
 * `secondsUntilDeathS` = camtime (5.0 for the final killcam).
 */
export function finalKillcamGameTime(realS: number, secondsUntilDeathS: number): number {
  if (realS <= 0) return 0;
  const segs = finalSegments(secondsUntilDeathS);
  for (const s of segs) {
    if (realS < s.r0 + s.durS) {
      const x = realS - s.r0;
      // integral of a linear scale ramp: s0*x + (s1-s0)*x^2/(2*dur)
      return s.g0 + s.s0 * x + ((s.s1 - s.s0) * x * x) / (2 * s.durS);
    }
  }
  const last = segs[segs.length - 1]!;
  return last.g0 + ((last.s0 + last.s1) / 2) * last.durS + (realS - (last.r0 + last.durS));
}

/** Real seconds the final killcam needs to play `lengthGameS` seconds of footage (killcamlength). */
export function finalKillcamRealDuration(lengthGameS: number, secondsUntilDeathS: number): number {
  // monotonic: bisection on finalKillcamGameTime
  let lo = 0, hi = lengthGameS * 8 + 10;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (finalKillcamGameTime(mid, secondsUntilDeathS) < lengthGameS) lo = mid; else hi = mid;
  }
  return hi;
}

function finalSegments(secondsUntilDeathS: number): Seg[] {
  const c = FINAL_SLOWMO;
  // 1) normal speed until the slow-down starts (game time = real time)
  const g1 = c.threadStartS + Math.max(0, secondsUntilDeathS - c.waitBeforeDeathS);
  const segs: Seg[] = [{ r0: 0, durS: g1, g0: 0, s0: 1, s1: 1 }];
  // 2) ramp 1.0 -> 0.25 over 2 real s
  let r = g1, g = g1;
  const rampDownGame = ((1 + c.slowScale) / 2) * c.rampDownS;
  // the hold (`wait 2.5` in game time from the first setslowmotion) may end inside the ramp in theory; not with
  // these constants (ramp = 1.25 game s < 2.5)
  segs.push({ r0: r, durS: c.rampDownS, g0: g, s0: 1, s1: c.slowScale });
  r += c.rampDownS; g += rampDownGame;
  // 3) hold at 0.25 until 2.5 game s after the first setslowmotion
  const holdGame = Math.max(0, c.holdS - rampDownGame);
  const holdReal = holdGame / c.slowScale;
  segs.push({ r0: r, durS: holdReal, g0: g, s0: c.slowScale, s1: c.slowScale });
  r += holdReal; g += holdGame;
  // 4) ramp 0.25 -> 1.0 over 1 real s, then normal speed (handled after the last segment)
  segs.push({ r0: r, durS: c.rampUpS, g0: g, s0: c.slowScale, s1: 1 });
  return segs;
}
